/**
 * Workspace switcher dropdown component
 */

import { Fragment, useEffect } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { useStore } from '@/store';
import { cn } from '@/lib/utils';
import type { Workspace } from '@/store/types';

export function WorkspaceSwitcher() {
  const workspaces = useStore((s) => s.workspaces);
  const currentWorkspace = useStore((s) => s.currentWorkspace);
  const isLoadingWorkspaces = useStore((s) => s.isLoadingWorkspaces);
  const switchWorkspace = useStore((s) => s.switchWorkspace);

  // Ensure workspaces is an array
  const workspacesList = Array.isArray(workspaces) ? workspaces : [];

  useEffect(() => {
    // Only fetch if we don't have workspaces and we're not already loading
    if (workspacesList.length === 0 && !isLoadingWorkspaces) {
      useStore
        .getState()
        .fetchWorkspaces()
        .catch((e) => console.error('Failed to load workspaces', e));
    }
  }, [workspacesList.length, isLoadingWorkspaces]);

  if (!currentWorkspace) {
    return null;
  }

  const handleWorkspaceChange = (workspace: Workspace) => {
    switchWorkspace(workspace.id);
  };

  return (
    <>
      <Listbox value={currentWorkspace} onChange={handleWorkspaceChange}>
        <div className="relative">
          <Listbox.Button className="relative w-full min-w-[200px] cursor-pointer rounded-lg bg-white py-2 pr-10 pl-3 text-left shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-2 focus:ring-blue-600 focus:outline-none sm:text-sm dark:bg-gray-800 dark:ring-gray-700">
            <span className="flex items-center gap-2">
              {currentWorkspace.icon && <span className="text-lg">{currentWorkspace.icon}</span>}
              <span className="block truncate font-medium text-gray-900 dark:text-gray-100">
                {currentWorkspace.name}
              </span>
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="ring-opacity-5 absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-base shadow-lg ring-1 ring-black focus:outline-none sm:text-sm dark:bg-gray-800">
              {workspacesList.map((ws) => (
                <Listbox.Option
                  key={ws.id}
                  className={({ active }) =>
                    cn(
                      'relative cursor-pointer py-2 pr-9 pl-3 select-none',
                      active
                        ? 'bg-blue-50 text-blue-900 dark:bg-blue-900/20 dark:text-blue-400'
                        : 'text-gray-900 dark:text-gray-100'
                    )
                  }
                  value={ws}
                >
                  {({ selected, active }) => (
                    <>
                      <span className="flex items-center gap-2">
                        <span
                          className={cn(
                            'block truncate',
                            selected ? 'font-semibold' : 'font-normal'
                          )}
                        >
                          {ws.name}
                        </span>
                      </span>
                      {selected && (
                        <span
                          className={cn(
                            'absolute inset-y-0 right-0 flex items-center pr-3',
                            active ? 'text-blue-600' : 'text-blue-600'
                          )}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </>
  );
}
